/* tslint:disable */

import * as _ from 'lodash';
import * as Sketchfab from 'sketchfab';

export type AnnotationDelegate = (api: SketchfabApi, index: number) => void;

export interface AnnotationSubscription
{
  name: string;
  func: AnnotationDelegate;
}

export interface Animation
{
  uid: string;
  name: string;
  duration: number;
}

export enum TextureQuailty
{
  low = 'low',
  sd = 'sd',
  hd = 'hd',
}

const gamma: number = 2.4;

export class SketchfabClientOptions implements InitializationOptions
{
  /** 
  * Setting to 0 will not automatically play animations when the viewer starts
  * Default: 1
  **/
  animation_autoplay?: number;

  /** 
   * Setting to [1 - 100] will automatically load that annotation when the viewer starts
   * Default: off
   **/
  annotation?: number;

  /** 
   * Setting to any number will start the Autopilot cycle with that duration, in seconds, at each annotation
   * Default: off
   **/
  annotation_cycle?: number;

  /** 
  * Setting to 0 will hide annotation tooltips by default
  * Default: 1
  **/
  annotation_tooltip_visible?: number;

  /** 
   * Setting to 0 will hide annotations by default
   * Default: 1
   **/
  annotations_visible?: number;

  /** 
   * Usage: Viewer API helper. When enabled, Data such as background UIDs, environment UIDs, annotation UIDs, and camera limit coordinates will be logged to the browser console (This is useful during Viewer API development)
   * Default: 0
   **/
  api_log?: number;

  /** 
   * Setting to any other number will cause the model to automatically spin around the z-axis after loading (The greater the number, the faster the spin. A negative number will reverse the spin direction)
   * Default: 0
   **/
  autospin?: number;

  /** 
   * Setting to 1 will make the model load immediately once the page is ready, rather than waiting for a user to click the Play button
   * Default: 0
   **/
  autostart?: number;

  /** 
   * Setting to 0 will skip the initial animation that occurs when a model is loaded, and immediately show the model in its default position
   * Default: 1
   **/
  camera?: number;

  /** 
  * Setting to 0 will not show the depth of field refocus circle animation on click (This is useful if you are making screenshots or screen recordings from the viewer and you do not want the DoF input feedback visible)
  * Default: 1
  **/
  dof_circle?: number;

  /** 
  * Setting to [0 - 100] will define the default walk speed in First Person mode
  * Default: 25
  **/
  fps_speed?: number;

  /** 
  * Setting to a positive number will limit all textures to that maximum resolution (longest side in pixels). This should be a "power of 2" value such as 32, 128, 256, 512, etc.
  * Default: 8192
  **/
  max_texture_size?: number;

  /** 
   * Setting to "fps"" will start the model in First Person mode by default
   * Default: "orbit"
   **/
  navigation?: string;

  /** 
   * Setting to 1 will force all resources (textures) to download before the scene is displayed (This is useful if you want to prevent your viewers from seeing low-resolution or missing textures during the model load time)
   * Default: 0
   **/
  preload?: number;

  /** 
   * Setting to 0 will prevent zooming with the scroll wheel (This is useful for allowing regular page scrolling over the viewer)
   * Default: 1
   **/
  scrollwhell?: number;

  /** 
   * Setting to 0 will hide the "Disable Viewer" button so that users cannot stop the 3D render once it is started
   * Default: 1
   **/
  ui_stop?: number;

  /** 
   * Setting to 1 will make the model's background transparent (This is useful for overlaying models on your website elements)
   * Default: 0
   **/
  transparent?: number;

  /** 
   * Setting to 0 will disable the double-clicking to focus the camera in the viewer
   * Default: 1
   **/
  double_click?: number;

  /** 
  * Setting to 1 will prevent panning the camera. This is similar to Camera Limits
  * Default: 0
  **/
  orbit_constraint_pan?: number;

  /** 
   * Setting to [-π/2 – π/2] will define the camera's pitch down rotation limit. This is similar to Camera Limits
   * Default: null
   **/
  orbit_constraint_pitch_down?: string;

  /** 
   * Setting to [-π/2 – π/2] will define the camera's pitch up rotation limit. This is similar to Camera Limits
   * Default: null
   **/
  orbit_constraint_pitch_up?: string;

  /** 
   * Setting to [-π – π] will define the camera's yaw left rotation limit. This is similar to Camera Limits
   * Default: null
   **/
  orbit_constraint_yaw_left?: string;

  /** 
   * Setting to [-π – π] will define the camera's yaw right rotation limit. This is similar to Camera Limits
   * Default: null
   **/
  orbit_constraint_yaw_right?: string;

  /** 
   * Setting to a positive number will define the camera zoom in limit (minimum distance from the model). This is similar to Camera Limits
   * Default: null
   **/
  orbit_constraint_zoom_in?: string;

  /** 
   * Setting to a positive number will define the camera zoom out limit (maximum distance from the model). This is similar to Camera Limits
   * Default: null
   **/
  orbit_constraint_zoom_out?: string;

  /** 
   * Setting to 0 will hide the animation menu and timeline
   * Default: 1
   **/
  ui_animations?: number;

  /** 
   * Setting to 0 will hide the Annotation menu
   * Default: 1
   **/
  ui_annotations?: number;

  /** 
   * Setting to 0 will hide all the viewer controls at the bottom of the viewer (Help, Settings, VR, Fullscreen, Annotations, and Animations)
   * Default: 1
   **/
  ui_controls?: number;

  /** 
   * Setting to 0 will prevent the viewer controls from disappearing when the camera moves or when the viewer is inactive for a few seconds
   * Default: 1
   **/
  ui_fadeout?: number;

  /** 
   * Setting to 0 will hide the Fullscreen button
   * Default: 1
   **/
  ui_fullscreen?: number;

  /** 
   * Setting to 0 will hide main control buttons in the bottom right of the viewer (Help, Settings, VR, Fullscreen)
   * Default: 1
   **/
  ui_general_controls?: number;

  /** 
   * Setting to 0 will hide the Help button
   * Default: 1
   **/
  ui_help?: number;

  /** 
   * Setting to 0 will always hide the viewer hint animation ("click & hold to rotate"). Setting to 1 will show the hint the first time per browser session (using a cookie). Setting to 2 will always show the hint
   * Default: 1
   **/
  ui_hint?: number;

  /** 
   * Setting to 0 will hide the model info bar at the top of the viewer
   * Default: 1
   **/
  ui_infos?: number;

  /** 
   * Setting to 0 will hide the inspector button. It will also prevent the Inspector from opening, and save loading time by not downloading the model's wireframe file unless wireframe is explicitly enabled in 3D Settings or the wireframe_preload option is enabled
   * Default: 1
   **/
  ui_inspector?: number;

  /** 
   * Setting to 0 will hide the viewer loading bars
   * Default: 1
   **/
  ui_loading?: number;

  /** 
   * Setting to 0 will hide the Settings button
   * Default: 1
   **/
  ui_settings?: number;

  /** 
   * Setting to 0 will hide the Sound button
   * Default: 1
   **/
  ui_sound?: number;

  /** 
   * Setting to 0 will hide the View in VR button
   * Default: 1
   **/
  ui_vr?: number;

  /** 
   * (Account Limitation: Business) Setting to 0 remove the link from the Sketchfab logo watermark
   * Default: 1
   **/
  ui_watermark_link?: number;

  /** 
   * (Account Limitation: Enterprise) Setting to 0 will hide the AR button
   * Default: 1
   **/
  ui_ar?: number;

  /** 
   * (Account Limitation: Enterprise) Setting to 0 will hide the AR popup's help link
   * Default: 1
   **/
  ui_ar_help?: number;

  /** 
   * (Account Limitation: Enterprise) Setting to 0 will hide the AR popup's QR code
   * Default: 1caad9
   **/
  ui_ar_qrcode?: number;

  /** 
   * (Account Limitation: Enterprise) Setting to a hexidecimal color code (without the #) or a HTML color name will change the color of the viewer loading bar
   * Default: 1
   **/
  ui_color?: string;

  /** 
  * (Account Limitation: Enterprise) Setting to 0 remove the Sketchfab logo watermark
  * Default: 1
  **/
  ui_watermark?: number;

  internal?: number;

  /**
   * Start the viewer in stereoscopic VR Mode built for Google Cardboard and similar devices
   * Default:0
   **/
  cardboard?: number;

 
  /**
  *
  **/
  continuous_render?: number;

  /**
  *
  **/
  stop_button?: number;

  /**
  *
  **/
  desc_button?: number;

  /**
  *
  **/
  ui_snapshots?: number;

  /**
  *
  **/
  ui_theatre?: number;


  constructor()
  {
    this.preload = 1;
    this.ui_infos = 0;
    this.ui_controls = 0;
    this.ui_stop = 0;
    this.autospin = 0;
    this.annotations_visible = 0;
    this.cardboard = 0;
    this.continuous_render = 0;
    this.ui_watermark = 1;
    this.internal = 0;
    this.transparent = 0;
    this.desc_button = 0;
    this.stop_button = 0;
    this.ui_theatre = 0;
    this.ui_snapshots = 0;
    this.ui_fadeout = 0;
    this.ui_inspector = 0;
  }
}

export class SketchfabClient
{
  readonly _modelId: string;
  readonly _sfClient: Sketchfab;
  readonly _sfClientVersion = '1.9.0';

  private _annotationFocusSubscribers: AnnotationSubscription[] = [];
  private _annotationBlurSubscribers: AnnotationSubscription[] = [];
  private _annotations: Annotation[] = [];
  private _materials: Material[] = [];
  private _textures: Texture[] = [];
  private _animations: Animation[] = [];
  private _stopSpin: boolean = false;
  private _annotationTimer: number;
  private _sfApi: SketchfabApi;

  get materials(): Material[]
  {
    return this._materials;
  }

  get annotations(): Annotation[]
  {
    return this._annotations;
  }

  get textures(): Texture[]
  {
    return this._textures;
  }

  get animations(): Animation[]
  {
    return this._animations;
  }

  constructor(iframe: Element, modelId: string)
  {

    this._modelId = modelId;

    this._sfClient = new Sketchfab(this._sfClientVersion, iframe);
  }

  private loadTextureList(api: SketchfabApi): Promise<Texture[]>
  {
    return new Promise((resolve, reject) =>
    {
      api.getTextureList((err, textureList) =>
      {
        if (err)
        {
          reject({
            source: 'loadTextureList',
            error: err,
          });
          return;
        }
        resolve(textureList);
      });
    });
  }

  private loadAnnotationList(api: SketchfabApi): Promise<Annotation[]>
  {
    return new Promise((resolve, reject) =>
    {
      api.getAnnotationList((err, annotations) =>
      {
        if (err)
        {
          reject({
            source: 'loadAnnotationList',
            error: err,
          });
          return;
        }
        resolve(annotations);
      });
    });
  }

  private loadAnimationList(api: SketchfabApi): Promise<Animation[]>
  {
    return new Promise((resolve, reject) =>
    {
      api.getAnimations((err, anms) =>
      {
        if (err)
        {
          reject({
            source: 'loadAnimationList',
            error: err,
          });
          return;
        }



        const animations = _.sortBy(_.map(anms, (a) =>
        {
          return <Animation>({ uid: a[0], name: a[1], duration: a[2] });
        }), (x) =>
        {
          return x.name;
        });

        resolve(animations);
      });
    });
  }

  private loadMaterialList(api: SketchfabApi): Promise<Material[]>
  {
    return new Promise((resolve, reject) =>
    {
      api.getMaterialList((err, materials) =>
      {
        if (err)
        {
          reject({
            source: 'loadMaterialList',
            error: err,
          });
          return;
        }
        resolve(materials);
      });
    });
  }

  private isWebGlSupported(): boolean
  {
    let canvas;
    let ctx;
    let exts;

    try
    {
      canvas = document.createElement('canvas');
      ctx = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      exts = ctx.getSupportedExtensions();
      return true;
    } catch (e)
    {
      return false;
    }
  }

  init(options: SketchfabClientOptions): Promise<SketchfabApi>
  {
    return new Promise((resolve, reject) =>
    {
      const apiOptons = <ApiOptions>(options);
      apiOptons.success = (api) =>
      {
        api.start();
        this._sfApi = api;
        api.start();
        const webGlSupport = this.isWebGlSupported();
        if (!webGlSupport)
        {
          reject({
            message: 'In your browser WebGl is not supported',
            webGlSupport,
          });
        }

        api.addEventListener('viewerready', () =>
        {
          (window as any)._sf = api;
          const materialPromise = this.loadMaterialList(api);
          const animationsPromise = this.loadAnimationList(api);
          const texturePromise = this.loadTextureList(api);
          const annotationsPromise = this.loadAnnotationList(api);


          api.addEventListener<number>('annotationFocus', (index) =>
          {
            _.each(this._annotationFocusSubscribers, (d) =>
            {
              if (d)
              {
                d.func.call(this, api, index);
              }
            });
          });

          api.addEventListener<number>('annotationBlur', (index) =>
          {
            _.each(this._annotationBlurSubscribers, (d) =>
            {
              if (d)
              {
                d.func.call(this, api, index);
              }
            });
          });

          Promise.all([materialPromise, texturePromise, annotationsPromise, animationsPromise])
            .then((res) =>
            {
              let materials: Material[];
              let textures: Texture[];
              let annotations: Annotation[];
              let animations: Animation[];

              [materials, textures, annotations, animations] = res;

              this._materials = materials;
              this._textures = textures;
              this._annotations = annotations;
              this._animations = animations;

              resolve(api);
            }).catch(function (err)
            {
              if (arguments.length > 0)
              {
                reject({
                  message: 'CODE:24 - Error while initializing the viewer',
                  errors: _.flatten(arguments),
                });
              }
            });
        });

      };
      apiOptons.error = (error) =>
      {
        reject(reject({
          message: 'CODE:23 - Error while initializing api',
          errors: error,
        }));
      };

      this._sfClient.init(this._modelId, apiOptons);

    });
  }

  unsubscribeFromAnnotationFocus(name: string): void
  {
    const s = _.find(this._annotationFocusSubscribers, s => s.name === name);
    if (!s)
    {
      return;
    }
    _.remove(this._annotationFocusSubscribers, s);
  }

  unsubscribeFromAnnotationBlur(name: string): void
  {
    const s = _.find(this._annotationBlurSubscribers, s => s.name === name);
    if (!s)
    {
      return;
    }
    _.remove(this._annotationBlurSubscribers, s);
  }

  subscribeToAnnotationFocus(name: string, func: AnnotationDelegate): void
  {
    this._annotationFocusSubscribers.push({
      name,
      func,
    });
  }

  subscribeToAnnotationBlur(name: string, func: AnnotationDelegate): void
  {
    this._annotationBlurSubscribers.push({
      name,
      func,
    });
  }

  getMaterial(name: string): Material
  {
    const material = _.find(this._materials, (m) =>
    {
      return m.name === name;
    });
    return <Material>material;
  }

  addTexture(url: string): Promise<string>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.addTexture(url, (err, textureUid) =>
      {
        if (err)
        {
          reject({
            source: 'addTexture',
            error: err,
          });
          return;
        }
        resolve(textureUid);
      });
    });
  }

  getTexture(name: string): Texture
  {
    const texture = _.find(this._textures, (t) =>
    {
      return t.name === name;
    });
    return <Texture>texture;
  }

  setMaterial(material: Material): Promise<boolean>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.setMaterial(material, (err) =>
      {
        if (err)
        {
          reject({
            source: 'setMaterial',
            error: err,
          });
          return;
        }
        resolve(true);
      });
    });
  }

  translate(instanceID: string, [axisX, axisY, axisZ]: [number, number, number], duration: number, easing: Easing): Promise<string>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.translate(instanceID, [axisX, axisY, axisZ], duration, easing, (err, translateTo) =>
      {
        if (err)
        {
          reject({
            source: 'translate',
            error: err,
          });
          return;
        }
        resolve(translateTo);
      });
    });
  }

  rotate(instanceID: string, [radAngle, axisX, axisY, axisZ]: [number, number, number, number], duration: number, easing: Easing): Promise<string>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.rotate(instanceID, [radAngle, axisX, axisY, axisZ], duration, easing, (err, rotateTo) =>
      {
        if (err)
        {
          reject({
            source: 'rotate',
            error: err,
          });
          return;
        }
        resolve(rotateTo);
      });
    });
  }

  setUVScale(mmaterialToUpdate: Material, channel: string, scaleX: number, scaleY: number): Promise<boolean>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.setUVScale(mmaterialToUpdate, channel, scaleX, scaleY, (err) =>
      {
        if (err)
        {
          reject({
            source: 'setUVScale',
            error: err,
          });
          return;
        }
        resolve(true);
      });
    });
  }

  setUVOffset(mmaterialToUpdate: Material, channel: string, offsetX: number, offsetY: number): Promise<boolean>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.setUVOffset(mmaterialToUpdate, channel, offsetX, offsetY, (err) =>
      {
        if (err)
        {
          reject({
            source: 'setUVOffset',
            error: err,
          });
          return;
        }
        resolve(true);
      });
    });
  }

  setUVRotation(mmaterialToUpdate: Material, channel: string, rotation: number): Promise<boolean>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.setUVRotation(mmaterialToUpdate, channel, rotation, (err) =>
      {
        if (err)
        {
          reject({
            source: 'setUVRotation',
            error: err,
          });
          return;
        }
        resolve(true);
      });
    });
  }

  hideObject(instanceId: string): Promise<boolean>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.hide(instanceId, (err) =>
      {
        if (err)
        {
          reject({
            source: 'hideObject',
            error: err,
          });
          return;
        }
        resolve(true);
      });
    });
  }

  showObject(instanceId: string): Promise<boolean>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.show(instanceId, (err) =>
      {
        if (err)
        {
          reject({
            source: 'showObject',
            error: err,
          });
          return;
        }
        resolve(true);
      });
    });
  }

  setPostProcessing(settings: PostProcessing): Promise<boolean>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.setPostProcessing(settings, () =>
      {
        resolve(true);
      });
    });
  }

  getNodeMap(): Promise<NodeMap[]>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.getNodeMap((err, nodes) =>
      {
        if (err)
        {
          reject({
            source: 'getNodeMap',
            error: err,
          });
          return;
        }
        resolve(nodes);
      });
    });
  }

  gotoAnnotation(index: number): Promise<boolean>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.gotoAnnotation(index, { preventCameraAnimation: false, preventCameraMove: false }, (err, index) =>
      {
        if (err)
        {
          reject({
            source: 'gotoAnnotation',
            error: err,
          });
          return;
        }
        resolve(true);
      });
    });
  }

  setAnimation(animationId: string): Promise<boolean>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.setCurrentAnimationByUID(animationId, (err) =>
      {
        if (err)
        {
          reject({
            source: 'setAnimation',
            error: err,
          });
          return;
        }
        resolve(true);
      });
    });
  }

  getScreenShot(mimeType: string = 'image/jpeg', width?: number, height?: number): Promise<string>
  {
    return new Promise((resolve, reject) =>
    {

      this._sfApi.getScreenShot(width, height, mimeType, (err, result) =>
      {
        if (err)
        {
          reject({
            source: 'getScreenShot',
            error: err,
          });
          return;
        }
        resolve(result);
      });
    });
  }

  setTextureQuality(quality: TextureQuailty): Promise<boolean>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.setTextureQuality(quality.toString(), (err, message) =>
      {
        if (err)
        {
          reject({
            source: 'setTextureQuality',
            error: err,
          });
          return;
        }
        resolve(true);
      });
    });
  }

  public stopAutoSpin(): void
  {
    this._stopSpin = true;
  }

  public pauseAnimation(): Promise<boolean>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.pause((err) =>
      {
        if (err)
        {
          reject({
            source: 'pauseAnimation',
            error: err,
          });
          return;
        }
        resolve(true);
      });
    });
  }

  public getCurrentTimeOfAnimation(): Promise<number>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.getCurrentTime((err, num) =>
      {
        if (err)
        {
          reject({
            source: 'getCurrentTimeOfAnimation',
            error: err,
          });
          return;
        }
        resolve(num);
      });
    });
  }

  public playAnimation(): Promise<boolean>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.play((err) =>
      {
        if (err)
        {
          reject({
            source: 'playAnimation',
            error: err,
          });
          return;
        }
        resolve(true);
      });
    });
  }

  public seekAnimationTo(seconds: number): Promise<boolean>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.seekTo(seconds, (err) =>
      {
        if (err)
        {
          reject({
            source: 'seekAnimationTo',
            error: err,
          });
          return;
        }
        resolve(true);
      });
    });
  }

  public setAnimationsetSpeed(speed: number): Promise<number>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.setSpeed(speed, (err, s) =>
      {
        if (err)
        {
          reject({
            source: 'setAnimationsetSpeed',
            error: err,
          });
          return;
        }
        resolve(s);
      });
    });
  }

  public setAnimationCycleMode(cycle: Cycles): Promise<boolean>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.setCycleMode(cycle, (err) =>
      {
        if (err)
        {
          reject({
            source: 'setAnimationCycleMode',
            error: err,
          });
          return;
        }
        resolve(true);
      });
    });
  }

  public startAutoAnnotation(millisecond: number): void
  {
    let annotationIndex = 0;
    if (!this._annotations || this._annotations.length === 0)
    {
      return;
    }

    this._annotationTimer = window.setInterval(() =>
    {
      if (annotationIndex >= this._annotations.length)
      {
        annotationIndex = 0;
      }
      this.gotoAnnotation(annotationIndex).then((i) =>
      {
        annotationIndex = annotationIndex + 1;
      });
    }, millisecond);
  }

  public stopAutoAnnotation(): void
  {
    clearInterval(this._annotationTimer);
  }

  public lookat([positionX, positionY, positionZ]: [number, number, number],
    [targetX, targetY, targetZ]: [number, number, number], duration: number): Promise<any>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.lookat([positionX, positionY, positionZ], [targetX, targetY, targetZ], duration, (err) =>
      {
        if (err)
        {
          reject({
            source: 'lookat',
            error: err,
          });
          return;
        }
        resolve(true);
      });
    });
  }

  public getCameraLookAt(): Promise<Camera>
  {
    return new Promise((resolve, reject) =>
    {
      this._sfApi.getCameraLookAt((err, camera) =>
      {

        if (err)
        {
          reject({
            source: 'getCameraLookAt',
            error: err,
          });
          return;
        }
        resolve(camera);
      });
    });
  }

  public startAutoSpin(dueTime: number = 3000): void
  {
    const cameraSpeed: number = .05;
    let cameraRadians: number = 0;
    let cameraDistance: number = 0;

    const spinFunction = () =>
    {
      this._sfApi.getCameraLookAt((err, camera) =>
      {
        const cameraPos = camera.position;
        const targetPos = camera.target;

        const targetX = targetPos[0];
        const targetY = targetPos[1];
        const cameraX = cameraPos[0];
        const cameraY = cameraPos[1];
        const cameraZ = cameraPos[2];
        const diffX = cameraX - targetX;
        const diffY = cameraY - targetY;

        cameraDistance = Math.sqrt(Math.pow(diffX, 2) + Math.pow(diffY, 2));

        if (cameraRadians < Math.PI * 2)
        {
          cameraRadians += cameraSpeed;
        } else
        {
          cameraRadians = 0;
        }

        const x = targetX + Math.cos(cameraRadians) * cameraDistance;
        const y = targetY + Math.sin(cameraRadians) * cameraDistance;

        const pos = [x, y, cameraZ] as [number, number, number];
        if (this._stopSpin)
        {
          return;
        }
        this._sfApi.lookat(pos, targetPos, 1, (err) =>
        {

          if (!this._stopSpin)
          {

            spinFunction();
          }
        });
      });
    };

    window.setTimeout(() =>
    {

      this._sfApi.getCameraLookAt((err: string, camera: Camera) =>
      {
        const cameraPos = camera.position;
        const targetPos = camera.target;

        const targetX = targetPos[0];
        const targetY = targetPos[1];

        const cameraX = cameraPos[0];
        const cameraY = cameraPos[1];
        const cameraZ = cameraPos[2];

        const diffX = cameraX - targetX;
        const diffY = cameraY - targetY;

        cameraDistance = Math.sqrt(Math.pow(diffX, 2) + Math.pow(diffY, 2));
        this._stopSpin = false;
        spinFunction();
      });
    }, dueTime);
  }

  public lockZAxis(zFrom: number, zToLock: number, interval: number, cameraDurationInSeconds: number = 1): void
  {
    window.setInterval(() =>
    {
      this._sfApi.getCameraLookAt((err, camera) =>
      {
        const cameraPos = camera.position;
        const targetPos = camera.target;
        const z = cameraPos[2];
        if (z < zFrom)
        {
          cameraPos[2] = zToLock;
          this._sfApi.lookat(
            cameraPos, // eye position
            targetPos,   // target to lookat
            cameraDurationInSeconds,          // duration of the animation in seconds
          );
        }
      });
    }, interval);
  }

  static linearToSrgb(linear: number)
  {
    let v = 0.0;
    if (linear < 0.0031308)
    {
      if (linear > 0.0)
      {
        v = linear * 12.92;
      }
    } else
    {
      v = 1.055 * Math.pow(linear, 1.0 / gamma) - 0.055;
    }
    return v;
  };

  static srgbToLinear(sgrb: number)
  {
    let v = 0.0;
    if (sgrb < 0.04045)
    {
      if (sgrb >= 0.0)
      {
        v = sgrb * (1.0 / 12.92);
      }
    } else
    {
      v = Math.pow((sgrb + 0.055) * (1.0 / 1.055), gamma);
    }
    return v;
  };

  static hexToRgb(hex: string): { r: number, g: number, b: number }
  {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    let shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) =>
    {
      return r + r + g + g + b + b;
    });

    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  static hexToLinear(hex: string): Float64Array
  {
    let rgb = this.hexToRgb(hex);
    if (rgb == null)
    {
      return null;
    }

    let r = this.srgbToLinear(rgb.r);
    let g = this.srgbToLinear(rgb.g);
    let b = this.srgbToLinear(rgb.b);
    return new Float64Array([r, g, b]);
  }

}




// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/services/sketchfab/SketchfabClient.ts